import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// 🔹 Tipo de cada elemento del índice
export type SearchItem = {
  id: string;
  title: string;
  content?: string;
  path?: string;
  type?: string;
  tags?: string[];
};

// 🔹 Tipo del contexto
interface SearchContextType {
  query: string;
  setQuery: (q: string) => void;
  results: SearchItem[];
  registerItem: (item: SearchItem) => void;
  unregisterItem: (id: string) => void;
}

// 🔹 Contexto
const SearchContext = createContext<SearchContextType | undefined>(undefined);

// 🔹 Hook
export const useSearch = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch debe usarse dentro de SearchProvider");
  return ctx;
};

// 🔹 Provider
export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState<SearchItem[]>([]);
  const [results, setResults] = useState<SearchItem[]>([]);

  // 🔥 NORMALIZADOR GLOBAL (clave para que funcione bien la búsqueda)
  const normalize = (v: any) =>
    (v ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .trim();

  // 🔹 Registrar item
  const registerItem = (item: SearchItem) => {
    setIndex((prev) => {
      const existing = prev.find((i) => i.id === item.id);

      // Evitar re-render si no cambió
      if (
        existing &&
        existing.title === item.title &&
        existing.content === item.content &&
        JSON.stringify(existing.tags ?? []) ===
        JSON.stringify(item.tags ?? [])
      ) {
        return prev;
      }

      const filtered = prev.filter((i) => i.id !== item.id);
      return [...filtered, item];
    });
  };

  // 🔹 Eliminar item
  const unregisterItem = (id: string) => {
    setIndex((prev) => prev.filter((i) => i.id !== id));
  };

  // 🔥 BÚSQUEDA ROBUSTA
  const performSearch = (q?: string): SearchItem[] => {
    const queryStr = normalize(q ?? query);
    if (!queryStr) return [];

    return index.filter((item) => {
      const haystack = [
        item.title,
        item.content,
        ...(item.tags ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(queryStr);
    });
  };
  // 🔹 Debounce de búsqueda
  useEffect(() => {
    const id = setTimeout(() => {
      setResults(performSearch());
    }, 250);

    return () => clearTimeout(id);
  }, [query, index]);

  // 🔹 Contexto optimizado
  const value = useMemo(
    () => ({
      query,
      setQuery,
      results,
      registerItem,
      unregisterItem,
    }),
    [query, results] // ❗ FIX: quitamos index (no hacía falta)
  );

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};